interface Client {
  name: string;
  property: string;
}

interface Props {
  client: Client;
  onTabChange: (tab: string) => void;
}

const stats = [
  { label: 'Monthly Rent', value: '$8,500', sub: 'Due 1st of month', icon: 'ri-money-dollar-circle-line', trend: 'stable' },
  { label: 'Occupancy Status', value: 'Occupied', sub: 'Lease ends Dec 2025', icon: 'ri-home-4-line', trend: 'good' },
  { label: 'Open Maintenance', value: '1 Request', sub: 'In progress', icon: 'ri-tools-line', trend: 'neutral' },
  { label: 'YTD Net Income', value: '$89,250', sub: 'After management fees', icon: 'ri-bar-chart-2-line', trend: 'good' },
];

const recentActivity = [
  { date: 'Mar 28, 2026', type: 'Payment', description: 'March rent received — $8,500', status: 'Completed', icon: 'ri-check-line', color: 'text-emerald-600 bg-emerald-50' },
  { date: 'Mar 22, 2026', type: 'Maintenance', description: 'HVAC filter replacement scheduled', status: 'In Progress', icon: 'ri-time-line', color: 'text-amber-600 bg-amber-50' },
  { date: 'Mar 15, 2026', type: 'Inspection', description: 'Quarterly property inspection completed', status: 'Completed', icon: 'ri-check-line', color: 'text-emerald-600 bg-emerald-50' },
  { date: 'Mar 01, 2026', type: 'Report', description: 'February financial report published', status: 'Available', icon: 'ri-file-line', color: 'text-[#8B7355] bg-[#F5F0E8]' },
  { date: 'Feb 28, 2026', type: 'Payment', description: 'February rent received — $8,500', status: 'Completed', icon: 'ri-check-line', color: 'text-emerald-600 bg-emerald-50' },
];

export default function OverviewTab({ client, onTabChange }: Props) {
  const firstName = client.name.split(' ')[0];

  return (
    <div className="flex flex-col gap-8">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Welcome back, {firstName}
          </h2>
          <p className="text-sm text-[#6B6B6B] font-light mt-1">
            Here&apos;s a summary of your portfolio as of April 2026
          </p>
        </div>
        <div className="inline-flex items-center gap-2 bg-[#F5F0E8] border border-[#E0D5C5] rounded-full px-4 py-2">
          <i className="ri-building-line text-[#8B7355] text-sm"></i>
          <span className="text-xs text-[#8B7355] font-medium">{client.property}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white border border-[#E8E6E3] rounded-lg p-6 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F5F3F0]">
                <i className={`${stat.icon} text-[#8B7355] text-base`}></i>
              </div>
              {stat.trend === 'good' && <i className="ri-arrow-up-line text-emerald-500 text-sm"></i>}
              {stat.trend === 'stable' && <i className="ri-subtract-line text-[#AEAAA4] text-sm"></i>}
              {stat.trend === 'neutral' && <i className="ri-information-line text-amber-500 text-sm"></i>}
            </div>
            <div>
              <p className="text-xl font-semibold text-[#1A1A1A]">{stat.value}</p>
              <p className="text-xs text-[#9A9A9A] font-light mt-0.5">{stat.label}</p>
            </div>
            <p className="text-xs text-[#AEAAA4]">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Two-column lower section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white border border-[#E8E6E3] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Recent Activity</h3>
          <div className="flex flex-col divide-y divide-[#F0EDE9]">
            {recentActivity.map((item, i) => (
              <div key={i} className="flex items-start gap-4 py-4 first:pt-0 last:pb-0">
                <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${item.color}`}>
                  <i className={`${item.icon} text-xs`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1A1A1A] font-medium">{item.description}</p>
                  <p className="text-xs text-[#9A9A9A] mt-0.5">{item.date} · {item.type}</p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${item.color}`}>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-[#E8E6E3] rounded-lg p-6 flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em]">Quick Access</h3>
          {[
            { label: 'View Financial Reports', icon: 'ri-bar-chart-line', tab: 'financials' },
            { label: 'Maintenance Requests', icon: 'ri-tools-line', tab: 'maintenance' },
            { label: 'Lease Documents', icon: 'ri-file-text-line', tab: 'documents' },
            { label: 'Property Details', icon: 'ri-building-line', tab: 'property' },
          ].map((action) => (
            <button
              key={action.tab}
              onClick={() => onTabChange(action.tab)}
              className="flex items-center gap-3 p-3.5 rounded-md border border-[#E8E6E3] hover:border-[#8B7355] hover:bg-[#FDFCFA] transition-all cursor-pointer group text-left"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#F5F3F0] group-hover:bg-[#F5F0E8] transition-colors">
                <i className={`${action.icon} text-[#8B7355] text-sm`}></i>
              </div>
              <span className="text-sm text-[#2C2C2C] font-medium flex-1">{action.label}</span>
              <i className="ri-arrow-right-line text-[#AEAAA4] text-sm group-hover:text-[#8B7355] transition-colors"></i>
            </button>
          ))}

          {/* Manager Contact */}
          <div className="mt-2 p-4 bg-[#F5F3F0] rounded-md">
            <p className="text-xs text-[#6B6B6B] font-medium mb-1">Your Relationship Manager</p>
            <p className="text-sm text-[#1A1A1A] font-semibold">Alexandra Moore</p>
            <p className="text-xs text-[#9A9A9A] mt-0.5">a.moore@higherstandardglobal.com</p>
            <a
              href="mailto:a.moore@higherstandardglobal.com"
              className="mt-3 flex items-center gap-1.5 text-xs text-[#8B7355] hover:text-[#6B5A42] transition-colors cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              Send a message
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
